import { useRef, useState } from 'react'
import { ChildData } from 'react-router-navigation-prompt'
import { Button, CircularProgress, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@carsys/ui'
import { useFeature } from '@cbgms/base/feature-flags'

interface Extra {
  saveBeforeNavigate?: () => Promise<void>
  deleteBeforeNavigate?: () => Promise<void>
}

export default function ConfirmNavigationOrderModal({
  isActive,
  onCancel,
  onConfirm,
  saveBeforeNavigate,
  deleteBeforeNavigate
}: ChildData & Extra) {
  const hasB2BOrderPopup = useFeature('b2b-order-exit-popup')
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Refs for buttons
  const deleteBtnRef = useRef<HTMLButtonElement>(null)
  const saveBtnRef = useRef<HTMLButtonElement>(null)
  const cancelBtnRef = useRef<HTMLButtonElement>(null)

  // Collect refs in order for navigation
  const buttonRefs = [cancelBtnRef, saveBtnRef, deleteBeforeNavigate ? deleteBtnRef : null].filter(
    Boolean
  ) as React.RefObject<HTMLButtonElement>[]

  // Handle left/right arrow key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const activeIdx = buttonRefs.findIndex(ref => ref.current === document.activeElement)
      if (activeIdx !== -1) {
        e.preventDefault()
        const dir = e.key === 'ArrowLeft' ? -1 : 1
        const nextIdx = (activeIdx + dir + buttonRefs.length) % buttonRefs.length
        buttonRefs[nextIdx].current?.focus()
      }
    }
  }

  const handleSaveContinue = async () => {
    try {
      setSaving(true)
      if (saveBeforeNavigate) {
        await saveBeforeNavigate()
      }
      onConfirm()
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteContinue = async () => {
    if (!deleteBeforeNavigate) return
    try {
      setDeleting(true)
      await deleteBeforeNavigate()
      onConfirm()
    } finally {
      setDeleting(false)
    }
  }

  if (!hasB2BOrderPopup) {
    return null
  }

  return (
    <Dialog open={isActive}>
      <DialogTitle>{t('Form.OrderForm.NavigationConfirmation.Title')}</DialogTitle>
      <Divider />
      <DialogContent>
        <DialogContentText>{t('Form.OrderForm.NavigationConfirmation.Content')}</DialogContentText>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction='row' spacing={1} width='100%' justifyContent='flex-end' onKeyDown={handleKeyDown}>
          <Button
            variant='text'
            color='primary'
            disableFocusRipple
            onClick={onCancel}
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              '&:focus-visible': {
                boxShadow: '0 0 0 2px #1976d2'
              }
            }}
            ref={cancelBtnRef}
          >
            {t('Form.OrderForm.NavigationConfirmation.KeepEditing')}
          </Button>
          <Button
            variant='outlined'
            color='primary'
            disableFocusRipple
            onClick={handleSaveContinue}
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              '&:focus-visible': {
                boxShadow: '0 0 0 2px #1976d2'
              }
            }}
            disabled={saving}
            ref={saveBtnRef}
          >
            {saving ? <CircularProgress size={16} /> : null}
            {t('Form.OrderForm.NavigationConfirmation.SaveAndContinue')}
          </Button>
          {deleteBeforeNavigate ? (
            <Button
              variant='contained'
              color='primary'
              disableFocusRipple
              onClick={handleDeleteContinue}
              autoFocus
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                '&:hover,&:focus': {
                  backgroundColor: 'primary.dark'
                },
                '&:focus-visible': {
                  boxShadow: '0 0 0 2px #1976d2'
                }
              }}
              disabled={deleting}
              ref={deleteBtnRef}
            >
              {deleting ? <CircularProgress size={16} /> : null}
              {t('Form.OrderForm.NavigationConfirmation.DeleteAndContinue')}
            </Button>
          ) : null}
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
